import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { updateCard, deleteCard as deleteCardApi, getCardById, updateLeadCaptureMode } from '../apis/cardApi';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';

// Create the CardContext
const CardContext = createContext();

export const CardProvider = ({ children }) => {
  const { getCurrentUserId, validateUserAccess, handleUnauthorizedAccess, isAuthenticated, authState } = useAuth();
  const { addError, handleApiCall } = useError();

  const [cardState, setCardState] = useState({});
  const [cardId, setCardId] = useState(null);
  const [cardError, setCardError] = useState(null);

  // ✅ FIXED: Enhanced request deduplication with cache
  const [pendingRequests, setPendingRequests] = useState(new Map());
  const [cardCache, setCardCache] = useState(new Map());
  const loadedCardsRef = useRef(new Set());

  // Only load from localStorage if user is authenticated (not during signup)
  useEffect(() => {
    if (authState.isAuthenticated && !isSignupFlow()) {
      const savedState = localStorage.getItem('cardState');
      if (savedState) {
        try {
          const parsedState = JSON.parse(savedState);
          setCardState(parsedState);
        } catch (error) {
          console.error('Error parsing cardState from localStorage:', error);
          setCardState({});
        }
      }
    } else if (!authState.isAuthenticated && !isSignupFlow()) {
      // Clear card state if not authenticated and not in signup flow
      setCardState({});
    }
    // During signup flow, don't load from localStorage - let the signup process build the state
  }, [authState.isAuthenticated]);

  // Initial values structure for each card
  const initialCardValues = {
    cardId: cardId,
    cardFullName: '',
    cardPronoun: 'he',
    cardPhoneNumber: '',
    cardEmail: '',
    cardJobTitle: '',
    cardCompany: '',
    cardLocation: '',
    cardProfilePictureUrl: '',
    cardProfilePictureImage: '',
    cardProfilePictureCroppedAreaPixels: '',
    cardCoverPhotoUrl: '',
    cardCompanyLogoUrl: '',
    cardName: '',
    contextCardLayout: 'left-aligned',
    cardBio: '',
    cardTheme: '',
    cardLinkColor: '#000000',
    cardTextColor: '',
    cardFont: 'Inter',
    cardBtnBackgroundColor: '',
    cardBtnSaveTextColor: '',
    cardMatchLinkIconsToTheme: false,
    cardUserId: '',
    cardIsActive: false,
    cardContactLinks: [],
    cardIsDefault: false,
    leadCaptureModeOn: false,
    autoFollowUpIntroEmail: false,
    customLinkTypeLabels: {
      'Contact': 'Contact',
      'Social': 'Social',
      'Products & Services': 'Products & Services'
    }
  };

  // Check if operation is during signup flow
  const isSignupFlow = useCallback(() => {
    return localStorage.getItem('signupInProgress') === 'true' ||
      cardId === 'tempCardId' ||
      !isAuthenticated();
  }, [cardId, isAuthenticated]);

  // Check if card is temporary (during signup or new card creation)
  const isTemporaryCard = useCallback((cardId) => {
    // Check for traditional temporary card IDs
    const isTraditionalTemp = cardId === 'tempCardId' || cardId?.startsWith('temp_');

    // Check for UUID format (new card creation)
    const isUUID = cardId && cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

    // Check for signup flow
    const isInSignup = isSignupFlow();

    const result = isTraditionalTemp || isUUID || isInSignup;

    return result;
  }, [isSignupFlow]);

  // Enhanced error handling for card operations
  const handleCardError = useCallback((error) => {
    console.error('Card operation error:', error);

    // Skip error handling during signup flow for expected errors
    if (isSignupFlow()) {
      // Skip authentication errors during signup
      if (error.message?.includes('Authentication required')) {
        console.log('🚀 CardContext: Suppressing authentication error during signup flow');
        return;
      }

      // Skip "Card not found" errors during signup
      if (error.message?.includes('Card not found') || error.message?.includes('not found')) {
        console.log('🚀 CardContext: Suppressing card not found error during signup flow');
        return;
      }

      // Skip expected signup flow errors
      if (error.message?.includes('Card not found during signup - this is expected')) {
        console.log('🚀 CardContext: Suppressing expected signup flow error');
        return;
      }

      // Skip session expired errors during signup
      if (error.message?.includes('session has expired')) {
        console.log('🚀 CardContext: Suppressing session expired error during signup flow');
        return;
      }
    }

    // Add error to global error context for non-signup flows
    addError(error, {
      source: 'card',
      type: 'card-operation',
      retryable: true,
      autoDismiss: false
    });

    if (error.response?.status === 401) {
      setCardError('Your session has expired. Please log in again');
      setTimeout(() => {
        window.location.href = '/sign-in';
      }, 2000);
      return;
    }

    if (error.response?.status === 403) {
      setCardError('You do not have permission to modify this card');
      handleUnauthorizedAccess();
      return;
    }

    if (error.message?.includes('permission')) {
      setCardError('You do not have permission to modify this card. Redirecting to your cards...');
      // Redirect to user's own cards after a short delay
      setTimeout(() => {
        window.location.href = '/dashboard/mycards';
      }, 2000);
      return;
    }

    if (error.message?.includes('session')) {
      setCardError('Your session has expired. Please log in again');
      setTimeout(() => {
        window.location.href = '/sign-in';
      }, 2000);
      return;
    }

    if (error.message?.includes('not found')) {
      setCardError('Card not found');
      return;
    }

    // Generic error handling
    setCardError(error.message || 'An error occurred while updating the card');
  }, [addError, handleUnauthorizedAccess, isSignupFlow]);

  // Clear error after timeout
  useEffect(() => {
    if (cardError) {
      const timer = setTimeout(() => {
        setCardError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [cardError]);

  // Initialize cardId from localStorage on mount
  useEffect(() => {
    // Only load cardData if user is authenticated and not in signup flow
    if (authState.isAuthenticated && !isSignupFlow()) {
      const savedCardData = localStorage.getItem('cardData');

      if (savedCardData) {
        try {
          const parsedCardData = JSON.parse(savedCardData);

          const currentUserId = getCurrentUserId();
          let validCard = null;

          if (currentUserId) {
            validCard = Object.values(parsedCardData).find(card =>
              card && card.cardId && card.cardId !== 'tempCardId' &&
              !card.cardId.startsWith('temp_') && card.cardUserId === currentUserId
            );
          }

          if (!validCard) {
            validCard = Object.values(parsedCardData).find(card =>
              card && card.cardId && card.cardId !== 'tempCardId' && !card.cardId.startsWith('temp_')
            );
          }

          if (validCard && validCard.cardId && validCard.cardId !== cardId) {
            setCardId(validCard.cardId);
          }
        } catch (error) {
          console.error('Error parsing cardData from localStorage:', error);
          setCardId('tempCardId');
        }
      }
    } else {
      // During signup flow, ensure we use tempCardId
      if (isSignupFlow()) {
        setCardId('tempCardId');
      }
    }
  }, [authState.isAuthenticated, cardId, getCurrentUserId, isSignupFlow]);

  // Add safety check for cardId
  const safeCardId = cardId || 'tempCardId';

  // Function to update the card state for a specific cardId with authorization
  const updateCardData = useCallback(async (cardId, newData, syncWithServer = false) => {
    try {
      setCardError(null);

      // Skip authentication check for temporary cards during signup
      if (!isTemporaryCard(cardId)) {
        const currentUserId = getCurrentUserId();
        const cardData = cardState[cardId];

        if (cardData && cardData.cardUserId) {
          try {
            validateUserAccess(cardData.cardUserId, currentUserId);
          } catch (error) {
            handleCardError(error);
            return;
          }
        }
      }

      // Update local state first
      setCardState((prevState) => {
        const newState = {
          ...prevState,
          [cardId]: {
            ...prevState[cardId],
            ...newData,
          },
        };

        // Save updated cardState to localStorage
        localStorage.setItem('cardState', JSON.stringify(newState));

        return newState;
      });

      // Optionally sync with server (only for non-temporary cards)
      if (syncWithServer && !isTemporaryCard(cardId)) {
        try {
          await handleApiCall(
            () => updateCard(cardId, newData),
            {
              source: 'card-update',
              retryable: true,
              autoDismiss: true
            }
          );
          console.log('Card updated successfully on server');
        } catch (error) {
          handleCardError(error);
          // Revert local changes if server update fails
          setCardState((prevState) => {
            const revertedState = { ...prevState };
            Object.keys(newData).forEach(key => {
              if (prevState[cardId] && prevState[cardId][key] !== undefined) {
                revertedState[cardId][key] = prevState[cardId][key];
              }
            });
            localStorage.setItem('cardState', JSON.stringify(revertedState));
            return revertedState;
          });
          throw error;
        }
      }
    } catch (error) {
      handleCardError(error);
      throw error;
    }
  }, [cardState, getCurrentUserId, handleApiCall, handleCardError, isTemporaryCard, validateUserAccess]);

  // Function to initialize a card with default values if it doesn't exist
  const initializeCard = useCallback((cardId) => {
    if (!cardState[cardId]) {
      setCardState((prevState) => {
        const newState = {
          ...prevState,
          [cardId]: {
            ...initialCardValues,
            cardId: cardId,
          },
        };

        localStorage.setItem('cardState', JSON.stringify(newState));
        return newState;
      });
    }
  }, [cardState, initialCardValues]);

  const updateCardId = useCallback((newCardId) => {
    setCardState((prevState) => {
      const oldCardData = prevState[cardId];

      const { [cardId]: removedOldEntry, ...rest } = prevState;

      const newState = {
        ...rest,
        [newCardId]: {
          ...oldCardData,
          cardId: newCardId,
        },
      };

      localStorage.setItem('cardId', newCardId);
      localStorage.setItem('cardState', JSON.stringify(newState));

      return newState;
    });

    setCardId(newCardId);
    console.log('Updated card ID in local storage:', localStorage.getItem('cardId'));
    console.log('Updated card state in local storage:', localStorage.getItem('cardState'));
  }, [cardId]);

  // Initialize card only if it's first created or retrieved from localStorage
  useEffect(() => {
    if (cardId && !cardState[cardId]) {
      initializeCard(cardId);
    }
  }, [cardId, cardState, initializeCard]);

  // Enhanced setter functions with authorization (but skip for temp cards)
  const createSecureCardUpdater = useCallback((fieldName) => {
    return async (cardId, value, syncWithServer = false) => {
      try {
        // Skip authentication check for temporary cards during signup
        if (!isTemporaryCard(cardId)) {
          const currentUserId = getCurrentUserId();
          const cardData = cardState[cardId];

          if (cardData && cardData.cardUserId) {
            try {
              validateUserAccess(cardData.cardUserId, currentUserId);
            } catch (error) {
              handleCardError(error);
              return;
            }
          }
        }

        await updateCardData(cardId, { [fieldName]: value }, syncWithServer);
      } catch (error) {
        console.error(`Failed to update ${fieldName}:`, error);

        // Don't throw authentication errors during signup
        if (!isSignupFlow() || !error.message?.includes('Authentication required')) {
          throw error;
        }
      }
    };
  }, [cardState, getCurrentUserId, handleCardError, isSignupFlow, isTemporaryCard, updateCardData, validateUserAccess]);

  // Setter functions for various attributes with authorization support
  const setCardFullName = useCallback(createSecureCardUpdater('cardFullName'), [createSecureCardUpdater]);
  const setCardPhoneNumber = useCallback(createSecureCardUpdater('cardPhoneNumber'), [createSecureCardUpdater]);
  const setCardPronoun = useCallback(createSecureCardUpdater('cardPronoun'), [createSecureCardUpdater]);
  const setCardEmail = useCallback(createSecureCardUpdater('cardEmail'), [createSecureCardUpdater]);
  const setCardJobTitle = useCallback(createSecureCardUpdater('cardJobTitle'), [createSecureCardUpdater]);
  const setCardCompany = useCallback(createSecureCardUpdater('cardCompany'), [createSecureCardUpdater]);
  const setCardLocation = useCallback(createSecureCardUpdater('cardLocation'), [createSecureCardUpdater]);
  const setCardProfilePictureUrl = useCallback(createSecureCardUpdater('cardProfilePictureUrl'), [createSecureCardUpdater]);
  const setCardProfilePictureImage = useCallback(createSecureCardUpdater('cardProfilePictureImage'), [createSecureCardUpdater]);
  const setCardProfilePictureCroppedAreaPixels = useCallback(createSecureCardUpdater('cardProfilePictureCroppedAreaPixels'), [createSecureCardUpdater]);
  const setCardCoverPhotoUrl = useCallback(createSecureCardUpdater('cardCoverPhotoUrl'), [createSecureCardUpdater]);
  const setCardCompanyLogoUrl = useCallback(createSecureCardUpdater('cardCompanyLogoUrl'), [createSecureCardUpdater]);
  const setCardName = useCallback(createSecureCardUpdater('cardName'), [createSecureCardUpdater]);
  const setContextCardLayout = useCallback(createSecureCardUpdater('contextCardLayout'), [createSecureCardUpdater]);
  const setCardBio = useCallback(createSecureCardUpdater('cardBio'), [createSecureCardUpdater]);
  const setCardTheme = useCallback(createSecureCardUpdater('cardTheme'), [createSecureCardUpdater]);
  const setCardLinkColor = useCallback(createSecureCardUpdater('cardLinkColor'), [createSecureCardUpdater]);
  const setCardTextColor = useCallback(createSecureCardUpdater('cardTextColor'), [createSecureCardUpdater]);
  const setCardFont = useCallback(createSecureCardUpdater('cardFont'), [createSecureCardUpdater]);
  const setCardBtnBackgroundColor = useCallback(createSecureCardUpdater('cardBtnBackgroundColor'), [createSecureCardUpdater]);
  const setCardBtnSaveTextColor = useCallback(createSecureCardUpdater('cardBtnSaveTextColor'), [createSecureCardUpdater]);
  const setCardMatchLinkIconsToTheme = useCallback(createSecureCardUpdater('cardMatchLinkIconsToTheme'), [createSecureCardUpdater]);
  const setCardUserId = useCallback(createSecureCardUpdater('cardUserId'), [createSecureCardUpdater]);
  const setCardIsActive = useCallback(createSecureCardUpdater('cardIsActive'), [createSecureCardUpdater]);
  const setCardIsDefault = useCallback(createSecureCardUpdater('cardIsDefault'), [createSecureCardUpdater]);
  const setAutoFollowUpIntroEmail = useCallback(createSecureCardUpdater('autoFollowUpIntroEmail'), [createSecureCardUpdater]);

  // Dedicated Lead Capture Mode handler
  const setLeadCaptureModeOn = useCallback(async (cardId, leadCaptureModeOn, syncWithServer = false) => {
    try {
      setCardError(null);

      // Skip authentication check for temporary cards during signup
      if (!isTemporaryCard(cardId)) {
        const currentUserId = getCurrentUserId();
        const cardData = cardState[cardId];

        if (cardData && cardData.cardUserId) {
          try {
            validateUserAccess(cardData.cardUserId, currentUserId);
          } catch (error) {
            handleCardError(error);
            return;
          }
        }
      }

      // Update local state first for immediate feedback
      setCardState((prevState) => {
        const newState = {
          ...prevState,
          [cardId]: {
            ...prevState[cardId],
            leadCaptureModeOn: leadCaptureModeOn,
          },
        };

        // Save updated cardState to localStorage
        localStorage.setItem('cardState', JSON.stringify(newState));
        return newState;
      });

      // Sync with server if requested and not a temporary card
      if (syncWithServer && !isTemporaryCard(cardId)) {
        try {
          await handleApiCall(
            () => updateLeadCaptureMode(cardId, leadCaptureModeOn),
            {
              source: 'lead-capture-update',
              retryable: true,
              autoDismiss: true
            }
          );
          console.log('Lead capture mode updated successfully on server');
        } catch (error) {
          handleCardError(error);
          // Revert local changes if server update fails
          setCardState((prevState) => {
            const revertedState = {
              ...prevState,
              [cardId]: {
                ...prevState[cardId],
                leadCaptureModeOn: !leadCaptureModeOn,
              },
            };
            localStorage.setItem('cardState', JSON.stringify(revertedState));
            return revertedState;
          });
          throw error;
        }
      }
    } catch (error) {
      handleCardError(error);
      throw error;
    }
  }, [cardState, getCurrentUserId, handleApiCall, handleCardError, isTemporaryCard, validateUserAccess]);

  // Special handler for custom labels
  const setCustomLinkTypeLabels = useCallback(async (cardId, customLinkTypeLabels, syncWithServer = false) => {
    try {
      // Skip authentication check for temporary cards during signup
      if (!isTemporaryCard(cardId)) {
        const currentUserId = getCurrentUserId();
        const cardData = cardState[cardId];

        if (cardData && cardData.cardUserId) {
          try {
            validateUserAccess(cardData.cardUserId, currentUserId);
          } catch (error) {
            handleCardError(error);
            return;
          }
        }
      }

      // FIXED: Allow empty values but ensure all three keys exist
      // This allows users to delete content while maintaining the structure
      const completeLabels = {
        'Contact': customLinkTypeLabels['Contact'] !== undefined ? customLinkTypeLabels['Contact'] : 'Contact',
        'Social': customLinkTypeLabels['Social'] !== undefined ? customLinkTypeLabels['Social'] : 'Social',
        'Products & Services': customLinkTypeLabels['Products & Services'] !== undefined ? customLinkTypeLabels['Products & Services'] : 'Products & Services'
      };
      await updateCardData(cardId, { customLinkTypeLabels: completeLabels }, syncWithServer);
    } catch (error) {
      console.error('Failed to update custom labels:', error);

      // Don't throw authentication errors during signup
      if (!isSignupFlow() || !error.message?.includes('Authentication required')) {
        throw error;
      }
    }
  }, [cardState, getCurrentUserId, handleCardError, isSignupFlow, isTemporaryCard, updateCardData, validateUserAccess]);

  // Clear card error
  const clearCardError = useCallback(() => {
    setCardError(null);
  }, []);

  // Enhanced card access validation (skip for temp cards)
  const validateCardAccess = useCallback((cardId, userId = getCurrentUserId()) => {
    // Skip validation for temporary cards
    if (isTemporaryCard(cardId)) {
      return true;
    }

    const cardData = cardState[cardId];
    if (!cardData) {
      throw new Error('Card not found');
    }

    // ✅ REMOVED: Frontend permission check - let backend handle authorization
    // Backend now properly handles team admin access to team member cards
    // if (cardData.cardUserId && cardData.cardUserId !== userId) {
    //   throw new Error('You do not have permission to access this card');
    // }

    return true;
  }, [cardState, getCurrentUserId, isTemporaryCard]);

  // Check if user owns the card (allow temp cards)
  const isCardOwner = useCallback((cardId, userId = getCurrentUserId()) => {
    if (isTemporaryCard(cardId)) {
      return true; // Anyone can own temp cards during signup
    }

    const cardData = cardState[cardId];
    return cardData && cardData.cardUserId === userId;
  }, [cardState, getCurrentUserId, isTemporaryCard]);

  // Get cards owned by current user
  const getUserCards = useCallback((userId = getCurrentUserId()) => {
    return Object.entries(cardState)
      .filter(([cardId, cardData]) =>
        isTemporaryCard(cardId) || cardData.cardUserId === userId
      )
      .map(([cardId, cardData]) => ({ cardId, ...cardData }));
  }, [cardState, getCurrentUserId, isTemporaryCard]);

  // Delete card with authorization and global error handling
  const deleteCard = useCallback(async (cardId) => {
    try {
      // Can't delete temporary cards via API
      if (isTemporaryCard(cardId)) {
        throw new Error('Cannot delete temporary cards');
      }

      const currentUserId = getCurrentUserId();
      const cardData = cardState[cardId];

      if (!cardData) {
        throw new Error('Card not found');
      }

      if (cardData.cardUserId !== currentUserId) {
        throw new Error('You do not have permission to delete this card');
      }

      await handleApiCall(
        () => deleteCardApi(cardId, currentUserId),
        {
          source: 'card-delete',
          retryable: true,
          autoDismiss: false
        }
      );

      // Remove from local state after successful server deletion
      setCardState((prevState) => {
        const newState = { ...prevState };
        delete newState[cardId];
        localStorage.setItem('cardState', JSON.stringify(newState));
        return newState;
      });

      console.log('Card deleted successfully:', cardId);
    } catch (error) {
      handleCardError(error);
      throw error;
    }
  }, [cardState, getCurrentUserId, handleApiCall, handleCardError, isTemporaryCard]);

  // ✅ CRITICAL FIX: Memoized loadCardFromServer with proper caching and deduplication
  const loadCardFromServer = useCallback(async (cardId) => {
    try {
      console.log('🔍 CardContext: loadCardFromServer called with cardId:', cardId);
      console.log('🔍 CardContext: isTemporaryCard result:', isTemporaryCard(cardId));

      if (isTemporaryCard(cardId)) {
        console.log('🔍 CardContext: Skipping load for temporary card:', cardId);
        return null;
      }

      // ✅ SUPER IMPORTANT: Check if we already loaded this card recently
      if (loadedCardsRef.current.has(cardId)) {
        // console.log('🔍 CardContext: Card already loaded recently, skipping:', cardId);
        return cardState[cardId] || null;
      }

      // ✅ Check cache first (5 minute cache)
      const cached = cardCache.get(cardId);
      if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) {
        // console.log('🔍 CardContext: Using cached card data for:', cardId);
        return cached.data;
      }

      // ✅ ADDED: Request deduplication to prevent multiple simultaneous requests
      if (pendingRequests.has(cardId)) {
        return await pendingRequests.get(cardId);
      }

      // Create a promise for this request
      const requestPromise = (async () => {
        // console.log('🔍 CardContext: Loading card from server:', cardId);

        const currentUserId = getCurrentUserId();
        const response = await handleApiCall(
          () => getCardById(cardId),
          {
            source: 'card-load',
            retryable: true,
            autoDismiss: true
          }
        );

        // console.log('🔍 CardContext: Server response:', response);

        // ✅ REMOVED: Frontend permission check - let backend handle authorization
        // Backend now properly handles team admin access to team member cards
        // if (response.card && response.card.userId && response.card.userId !== currentUserId) {
        //   throw new Error('You do not have permission to access this card');
        // }

        // FIXED: Properly map server response to CardContext format
        if (response.card) {
          const serverCard = response.card;

          const cardData = {
            ...initialCardValues,
            ...cardState[cardId], // Keep existing local changes
            // CRITICAL: Override with server values for these specific fields
            leadCaptureModeOn: Boolean(serverCard.leadCaptureModeOn),
            autoFollowUpIntroEmail: Boolean(serverCard.autoFollowUpIntroEmail),
            leadCaptureInputFields: serverCard.leadCaptureInputFields || [],
            leadCaptureFormHeader: serverCard.leadCaptureFormHeader || '',
            cardFullName: serverCard.contact_info?.fullName || '',
            cardJobTitle: serverCard.contact_info?.jobTitle || '',
            cardCompany: serverCard.contact_info?.company || '',
            cardPronoun: serverCard.contact_info?.pronoun || 'he',
            cardTheme: serverCard.cardTheme || '',
            cardProfilePictureUrl: serverCard.profilePictureUrl || '',
            cardCoverPhotoUrl: serverCard.coverPhotoUrl || '',
            cardCompanyLogoUrl: serverCard.companyLogoUrl || '',
            contextCardLayout: serverCard.cardLayout || 'left-aligned',
            cardLinkColor: serverCard.linkColor || '',
            cardLocation: serverCard.contact_info?.location || '',
            cardBio: serverCard.bio || '',
            customLinkTypeLabels: serverCard.customLinkTypeLabels || {
              'Contact': 'Contact',
              'Social': 'Social',
              'Products & Services': 'Products & Services'
            },
            cardFont: serverCard.font || 'Inter', // ✅ ADDED - Load font from server
            cardId: cardId,
          };

          setCardState((prevState) => {
            const newState = {
              ...prevState,
              [cardId]: cardData,
            };

            localStorage.setItem('cardState', JSON.stringify(newState));
            return newState;
          });

          // ✅ Cache the result
          setCardCache(prev => new Map(prev).set(cardId, {
            data: response.card,
            timestamp: Date.now()
          }));

          // ✅ Mark as recently loaded
          loadedCardsRef.current.add(cardId);

          // ✅ Clear the "recently loaded" flag after 30 seconds
          setTimeout(() => {
            loadedCardsRef.current.delete(cardId);
          }, 30000);
        }

        return response.card;
      })();

      // Store the promise in pending requests
      setPendingRequests(prev => new Map(prev).set(cardId, requestPromise));

      try {
        const result = await requestPromise;
        return result;
      } finally {
        // Clean up the pending request
        setPendingRequests(prev => {
          const newMap = new Map(prev);
          newMap.delete(cardId);
          return newMap;
        });
      }
    } catch (error) {
      handleCardError(error);
      throw error;
    }
  }, [cardState, cardCache, getCurrentUserId, handleApiCall, handleCardError, initialCardValues, isTemporaryCard, pendingRequests]);

  return (
    <CardContext.Provider
      value={{
        cardState: cardState || {},
        cardId: safeCardId,
        cardError,
        updateCardData,
        setCardFullName,
        setCardPhoneNumber,
        setCardPronoun,
        setCardEmail,
        setCardJobTitle,
        setCardCompany,
        setCardLocation,
        setCardProfilePictureUrl,
        setCardProfilePictureImage,
        setCardProfilePictureCroppedAreaPixels,
        setCardCoverPhotoUrl,
        setCardCompanyLogoUrl,
        setCardName,
        setContextCardLayout,
        setCardBio,
        setCardTheme,
        setCardLinkColor,
        setCardTextColor,
        setCardFont,
        setCardBtnBackgroundColor,
        setCardBtnSaveTextColor,
        setCardMatchLinkIconsToTheme,
        setCardUserId,
        setCardIsActive,
        setCardIsDefault,
        setLeadCaptureModeOn,
        setAutoFollowUpIntroEmail,
        updateCardId,
        setCardId: updateCardId,
        setCustomLinkTypeLabels,
        clearCardError,
        validateCardAccess,
        isCardOwner,
        getUserCards,
        deleteCard,
        loadCardFromServer,
        isTemporaryCard,
        isSignupFlow
      }}
    >
      {children}
    </CardContext.Provider>
  );
};

// Custom hook to access the CardContext
export const useCard = () => {
  const context = useContext(CardContext);
  if (!context) {
    throw new Error('useCard must be used within a CardProvider');
  }
  return context;
};

// Custom hook to use the CardContext
export const useCardContext = () => {
  const context = useContext(CardContext);
  if (!context) {
    throw new Error('useCardContext must be used within a CardProvider');
  }
  return context;
};